/**
 * «Hay una versión nueva de Softfi».
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL PROBLEMA QUE ESTO RESUELVE, Y CUÁNTO COSTÓ VERLO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Softfi se instala como aplicación (PWA) y guarda una copia de sí misma en el
 * dispositivo para poder vender sin internet. Esa copia es lo que hace que el
 * POS funcione cuando se cae la línea — y también lo que hacía que los arreglos
 * no llegaran nunca.
 *
 * El service worker sí se actualizaba solo. Lo que nadie hacía era **recargar
 * la página**, así que el navegador seguía ejecutando el JavaScript viejo que
 * ya tenía en memoria. Resultado: se corregía un fallo, se subía, se verificaba
 * en el servidor... y el dueño seguía viéndolo igual.
 *
 * Pasó tantas veces que llegó a sonar así:
 *
 *   «ayer como que no hiciste nada bien, o creo que no subiste las cosas»
 *
 * Y las cosas estaban subidas. Solo que no llegaban.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LA SEGUNDA VUELTA: EL AVISO TAMPOCO SALÍA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Este archivo se escribió para arreglar lo de arriba y no lo arregló, porque
 * estaba escrito para un service worker que ESPERA y el de Softfi estaba
 * configurado para activarse de golpe (`registerType: "autoUpdate"`). El aviso
 * se enseña cuando hay una copia esperando; como ninguna esperaba, nunca salía.
 *
 * El dueño lo dijo con las dos mitades juntas: «no me sale para actualizar y no
 * me sale actualizado».
 *
 * Ya está arreglado de raíz en `vite.config.ts` —ahora la copia nueva espera— y
 * aquí se le pusieron encima dos redes, porque una sola no bastó:
 *
 *   1. **El service worker**, como antes.
 *   2. **`version.json`**, que no depende de él. Treinta bytes con la fecha de
 *      compilación, pedidos por red y comparados con la que lleva dentro la
 *      copia que está corriendo. El service worker es una máquina de muchas
 *      piezas y cuando una falla no avisa; esto es una resta.
 *
 * Y el botón «Actualizar» ya no es una recarga a secas: le da paso a la copia
 * nueva, espera a que tome el mando y recarga. Si no hay ninguna esperando
 * —o sea, si algo se atascó— borra la copia guardada y recarga a lo bruto. Es
 * lo último que se intenta, pero se intenta.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  CUÁNDO SE RECARGA SOLA, Y CUÁNDO NUNCA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Al principio no se recargaba sola nunca: una recarga a traición en mitad de
 * un cobro se lleva los pagos a medio anotar, con el cliente delante. Pero en
 * el celular Softfi casi nunca se cierra, y los arreglos no llegaban a las
 * cajas. Así que ahora se actualiza sola SOLO en los momentos seguros —al
 * abrir, al volver después de un rato, y con la versión obligatoria cuando
 * nadie toca la pantalla—, y nunca con un cobro abierto ni algo a medias. Las
 * reglas están en `actualizacionSola.ts`.
 *
 * El aviso sigue: se puede tocar «Actualizar» en cualquier momento. Se puede
 * posponer, salvo si la versión es obligatoria (la fija el panel).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { cx } from "@/components/ui";
import {
  FUERA_MINIMO,
  QUIETO_EN_FORMULARIO,
  QUIETO_PARA_OBLIGADA,
  anotarIntento,
  esMasViejaQue,
  esMomentoSeguro,
  puedoIntentar,
  rutaConFormulario,
} from "./actualizacionSola";

/**
 * Cada cuánto se le pregunta al servidor si hay algo nuevo.
 *
 * ⚠️ Cinco minutos, y antes eran treinta. Se bajó porque media hora es mucho
 * tiempo cuando el dueño está esperando un arreglo que acaba de pedir, y lo que
 * se pide es un archivo de treinta bytes: una caja abierta doce horas hace 144
 * peticiones al día, menos de lo que pesa un icono.
 *
 * El service worker se consulta con el mismo reloj. `update()` no descarga nada
 * si no cambió.
 */
const CADA = 5 * 60 * 1000;

/** La fecha de la copia que está corriendo AHORA MISMO en esta pantalla. */
const MIA = __COMPILADO__;

/** Cuándo arrancó esta copia: para saber si Softfi «se acaba de abrir». */
const ABIERTA_EN = Date.now();

/** Cuánto dura «se acaba de abrir»: 15 segundos, si no se tocó nada. */
const RECIEN_ABIERTA = 15_000;

/** Cada cuánto se mira si ya se puede aplicar una versión obligatoria. */
const CADA_VISTAZO = 5_000;

/** Lo último que se sabe del servidor y del service worker. */
type LoQueSeSabe = {
  /** La fecha del servidor, si es otra que la mía. */
  suya: string | null;
  /** La versión mínima que exige el panel. */
  minima: string | null;
  /** Si el service worker tiene una copia nueva esperando. */
  swEsperando: boolean;
};

export function AvisoDeVersion({
  actualizarSola = true,
  traer = traerLaNueva,
}: {
  /**
   * Si se actualiza sola en los momentos seguros. Siempre, salvo en las
   * pruebas del letrero, que necesitan verlo sin que la página se recargue.
   */
  actualizarSola?: boolean;
  /** Cómo se trae la nueva. Solo para las pruebas. */
  traer?: () => Promise<"sin-internet" | void>;
} = {}) {
  const [hayNueva, setHayNueva] = useState(false);
  const [minima, setMinima] = useState<string | null>(null);
  const [pospuesto, setPospuesto] = useState(false);
  const [actualizando, setActualizando] = useState(false);
  const [sola, setSola] = useState(false);
  const [sinInternet, setSinInternet] = useState(false);

  const obligada = esMasViejaQue(MIA, minima);

  /*
    Lo mismo que el estado, pero leíble al instante desde los avisos del
    navegador: el estado de React no está al día dentro de un `await`.
  */
  const sabido = useRef<LoQueSeSabe>({ suya: null, minima: null, swEsperando: false });
  const ultimaInteraccion = useRef(0);
  /** Una actualización ya en marcha: no se lanza otra encima. */
  const enMarcha = useRef(false);

  const hayAlgo = () => {
    const s = sabido.current;
    return Boolean(s.suya) || s.swEsperando || esMasViejaQue(MIA, s.minima);
  };
  const esObligada = () => esMasViejaQue(MIA, sabido.current.minima);

  // ═══════════════════════════════════════════════════════════════════════
  //  RED 1: el archivo con la fecha, y la versión mínima del panel
  // ═══════════════════════════════════════════════════════════════════════
  //
  // No toca el service worker ni sabe que existe. Si la fecha del servidor no
  // es la mía, hay algo nuevo. Se acabó.
  const mirarLaFecha = useCallback(async () => {
    await Promise.all([
      (async () => {
        try {
          const r = await fetch("/version.json", { cache: "no-store" });
          if (!r.ok) return;
          const datos: unknown = await r.json();
          const suya = (datos as { compilado?: string })?.compilado;
          // Sin fecha suya no se concluye nada: mejor callarse que enseñar un
          // aviso que no se va nunca porque compara con `undefined`.
          if (typeof suya === "string" && suya && suya !== MIA) {
            sabido.current.suya = suya;
            setHayNueva(true);
          }
        } catch {
          // Sin internet no hay nada que mirar. Se vuelve a intentar solo.
        }
      })(),
      (async () => {
        try {
          const r = await fetch("/api/v1/publico/version-minima", { cache: "no-store" });
          if (!r.ok) return;
          const datos: unknown = await r.json();
          const m = (datos as { version_minima?: string | null })?.version_minima;
          const valor = typeof m === "string" && m ? m : null;
          sabido.current.minima = valor;
          setMinima(valor);
        } catch {
          // Igual: sin respuesta se deja lo que se sabía.
        }
      })(),
    ]);
  }, []);

  /**
   * Actualizar ya, sin preguntar — solo si es un momento seguro.
   *
   * ⚠️ Cada intento se anota: a la segunda vuelta fallida hacia la misma
   * versión se deja de intentar solo y queda el aviso con su botón. Ver
   * `puedoIntentar`.
   */
  const actualizarYa = useCallback(async () => {
    const s = sabido.current;
    const hacia = esMasViejaQue(MIA, s.minima) ? (s.minima as string) : (s.suya ?? "service-worker");
    if (enMarcha.current || !puedoIntentar(hacia) || !esMomentoSeguro()) return;
    enMarcha.current = true;
    anotarIntento(hacia);
    setSola(true);
    const resultado = await traer();
    // Si todo va bien, la página se recarga y esto no sigue. Sin internet no
    // se tocó nada: se vuelve a intentar en el siguiente momento seguro.
    if (resultado === "sin-internet") {
      enMarcha.current = false;
      setSola(false);
    }
  }, [traer]);

  /** Al abrir: si no se ha tocado nada todavía, se actualiza de una vez. */
  const siSeAcabaDeAbrir = useCallback(() => {
    if (!actualizarSola || !hayAlgo()) return;
    if (Date.now() - ABIERTA_EN > RECIEN_ABIERTA || ultimaInteraccion.current > 0) return;
    void actualizarYa();
  }, [actualizarSola, actualizarYa]);

  // ═══════════════════════════════════════════════════════════════════════
  //  RED 2: el service worker, y los momentos para actualizarse sola
  // ═══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    let registro: ServiceWorkerRegistration | undefined;
    let ocultaDesde = 0;

    function revisar() {
      void registro?.update();
      return mirarLaFecha();
    }

    // Al abrir, ya. Es el momento más frecuente: la persona acaba de entrar y
    // lleva días sin actualizar.
    void mirarLaFecha().then(siSeAcabaDeAbrir);

    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker.getRegistration().then((r) => {
        if (!r) return;
        registro = r;

        /*
          ⚠️ Se mira si YA hay una esperando.

          El service worker nuevo puede haberse instalado en la carga anterior y
          estar esperando desde entonces. Sin esta comprobación, el aviso solo
          aparecería si la actualización llega mientras la pestaña está abierta —
          justo el caso menos frecuente.
        */
        if (r.waiting) {
          sabido.current.swEsperando = true;
          setHayNueva(true);
          siSeAcabaDeAbrir();
        }

        r.addEventListener("updatefound", () => {
          const nuevo = r.installing;
          if (!nuevo) return;
          nuevo.addEventListener("statechange", () => {
            // `installed` con un controlador ya presente = hay una versión nueva
            // lista y otra funcionando. Sin controlador es la primera visita, y
            // ahí no hay nada que avisar.
            if (nuevo.state === "installed" && navigator.serviceWorker.controller) {
              sabido.current.swEsperando = true;
              setHayNueva(true);
            }
          });
        });

        void r.update();
      });
    }

    const temporizador = window.setInterval(() => void revisar(), CADA);

    // Lo que cuenta como «alguien está usando la pantalla».
    const tocar = () => {
      ultimaInteraccion.current = Date.now();
    };
    const eventos = ["pointerdown", "keydown", "touchstart", "wheel"] as const;
    for (const e of eventos) window.addEventListener(e, tocar, { passive: true, capture: true });

    /*
      ⚠️ AL VOLVER DESPUÉS DE UN RATO, SE ACTUALIZA SOLA.

      En el celular, Softfi casi nunca se cierra: el sistema lo deja vivo en
      segundo plano, y cerrar la pestaña no alcanza. Volver a él después de un
      rato en otra aplicación es el momento en que menos se pierde — siempre
      que no haya nada a medias (ver `esMomentoSeguro`) y que no se haya tocado
      nada desde que se volvió.
    */
    function alCambiarDeVista() {
      if (document.visibilityState === "hidden") {
        ocultaDesde = Date.now();
        return;
      }
      const fuera = ocultaDesde ? Date.now() - ocultaDesde : 0;
      const volvioEn = Date.now();
      void revisar().then(() => {
        if (!actualizarSola || fuera < FUERA_MINIMO || !hayAlgo()) return;
        if (ultimaInteraccion.current > volvioEn) return;
        // En una pantalla que es un formulario, solo si es obligatoria.
        if (!esObligada() && rutaConFormulario(window.location.pathname)) return;
        void actualizarYa();
      });
    }
    document.addEventListener("visibilitychange", alCambiarDeVista);
    const alConectar = () => void revisar();
    window.addEventListener("online", alConectar);

    /*
      ⚠️ LA VERSIÓN OBLIGATORIA, EN CUANTO NADIE TOQUE LA PANTALLA.

      Es la que fija el panel («Obligar a todos a actualizar»): la cajera
      parada en Vender sin hacer nada se actualiza a los 30 segundos. Nunca con
      un cobro abierto ni con algo a medias. En una pantalla que es un
      formulario, se espera mucho más.
    */
    const vistazo = window.setInterval(() => {
      if (!actualizarSola || !esObligada()) return;
      const quieta = Date.now() - Math.max(ultimaInteraccion.current, ABIERTA_EN);
      const hace_falta = rutaConFormulario(window.location.pathname)
        ? QUIETO_EN_FORMULARIO
        : QUIETO_PARA_OBLIGADA;
      if (quieta >= hace_falta) void actualizarYa();
    }, CADA_VISTAZO);

    return () => {
      window.clearInterval(temporizador);
      window.clearInterval(vistazo);
      for (const e of eventos) window.removeEventListener(e, tocar, { capture: true });
      document.removeEventListener("visibilitychange", alCambiarDeVista);
      window.removeEventListener("online", alConectar);
    };
  }, [mirarLaFecha, siSeAcabaDeAbrir, actualizarYa, actualizarSola]);

  // Mientras se actualiza sola: que nadie empiece nada que la recarga se lleve.
  if (sola) {
    return (
      <div
        className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-3 bg-surface/95"
        role="status"
      >
        <RefreshCw size={24} className="animate-spin text-accent" />
        <p className="text-sm font-semibold text-ink">Actualizando Softfi…</p>
      </div>
    );
  }

  if (!(hayNueva || obligada) || (pospuesto && !obligada)) return null;

  return (
    <div
      className={cx(
        /*
          ⚠️ EN CELULAR VA ARRIBA, NO ABAJO.

          Abajo estaba encima de la barra de «Cobrar» del punto de venta y del
          menú de abajo: con el aviso puesto, el cajero no podía cobrar hasta
          cerrarlo, y no sabía que eso era lo que lo tapaba. Lo encontró la
          auditoría. Arriba solo tapa el título de la página, que no se toca
          para vender.

          ⚠️ Y EN DOS FILAS: el texto arriba y el botón abajo, a lo ancho. En una
          sola fila, en un teléfono angosto el botón se salía de la pantalla y
          el letrero se cortaba (lo vio el dueño: «no salía el botón»).
        */
        "fixed inset-x-0 top-0 z-[60] flex flex-col gap-2.5 px-4 py-3",
        "border-b border-accent/30 bg-accent-soft",
        // La muesca y la hora del teléfono ocupan el borde de arriba.
        "pt-[calc(0.75rem+env(safe-area-inset-top))]",
        "sm:inset-x-auto sm:top-auto sm:bottom-4 sm:right-4 sm:max-w-sm sm:pt-3",
        "sm:rounded-[var(--radius-card)] sm:border sm:shadow-[var(--shadow-pop)]",
      )}
      role="status"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0 text-accent">
          <RefreshCw size={18} className={cx(actualizando && "animate-spin")} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-accent">
            {obligada ? "Hay que actualizar Softfi" : "Hay una versión nueva de Softfi"}
          </p>
          <p className="text-xs leading-relaxed text-ink-soft">
            {sinInternet
              ? "Hace falta internet para traer la versión nueva. Inténtalo cuando vuelva la conexión."
              : obligada
                ? "Se actualizará solo en cuanto termines lo que estás haciendo. No se pierde nada."
                : "Termina lo que estás haciendo y actualiza. No se pierde nada."}
          </p>
        </div>
        {/* La obligatoria no se pospone: para eso se pidió. */}
        {!obligada && (
          <button
            type="button"
            onClick={() => setPospuesto(true)}
            aria-label="Ahora no"
            className="-mr-1.5 -mt-1 shrink-0 rounded-lg p-1.5 text-muted transition-colors hover:bg-surface-hover hover:text-ink"
          >
            <X size={16} />
          </button>
        )}
      </div>
      <button
        type="button"
        disabled={actualizando}
        onClick={() => {
          setActualizando(true);
          setSinInternet(false);
          void traer().then((resultado) => {
            // Sin internet no se tocó nada: el botón vuelve y se dice por qué.
            if (resultado === "sin-internet") {
              setActualizando(false);
              setSinInternet(true);
            }
          });
        }}
        className="w-full rounded-[var(--radius-control)] bg-accent px-3 py-2 text-sm font-semibold text-on-accent disabled:opacity-70 sm:w-auto sm:self-end"
      >
        {actualizando ? "Actualizando…" : obligada ? "Actualizar ahora" : "Actualizar"}
      </button>
    </div>
  );
}

/**
 * Traer la versión nueva de verdad, cueste lo que cueste.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ NO BASTA CON `location.reload()`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque quien responde a esa recarga es el service worker VIEJO, que tiene
 * guardado el `index.html` viejo y lo devuelve tan contento. Se recarga la
 * página y sale exactamente lo mismo. Es lo que estaba pasando.
 *
 * Hay que darle paso primero a la copia nueva —`SKIP_WAITING`— y recargar
 * cuando ya haya tomado el mando. Tres pasos, en este orden, o no sirve.
 *
 * Se exporta para poder probarlo suelto: es la pieza que no puede fallar.
 *
 * ⚠️ La recarga entra como parámetro solo para poder probarla. `location` no se
 * puede sustituir en las pruebas —el navegador de mentira no deja tocarla— y
 * sin poder mirar si se recargó, esta función no se podría probar en absoluto.
 * En la aplicación nadie le pasa nada.
 */
/** Cuánto se espera a que termine de bajar la copia nueva: 20 segundos. */
const ESPERA_DE_LA_DESCARGA = 20_000;

/** La caché que usa `public/sw-transicion.js` como marca. No se borra nunca. */
const MARCA_DE_AL_DIA = "softfi-al-dia";

/**
 * Espera a que una copia que se está instalando termine: ya instalada, ya
 * activa o descartada. Con tope, porque en un aparato sin señal podría no
 * terminar nunca.
 */
function esperarQueTermine(copia: ServiceWorker, tope: number): Promise<void> {
  return new Promise((listo) => {
    const terminado = () => copia.state !== "installing";
    if (terminado()) return listo();
    const seguro = window.setTimeout(listo, tope);
    copia.addEventListener("statechange", () => {
      if (!terminado()) return;
      window.clearTimeout(seguro);
      listo();
    });
  });
}

/**
 * La versión nueva ya tomó el mando en este aparato —la activó otra pestaña—
 * y a esta solo le falta recargar. Lo avisa el navegador con
 * `controllerchange`. Ver «otra pestaña ya la trajo» en `traerLaNueva`.
 */
let yaCambioElMando = false;

/** Se exporta para las pruebas, que no pueden hacer que el navegador lo avise. */
export function cambioElMando(valor = true): void {
  yaCambioElMando = valor;
}

if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("controllerchange", () => cambioElMando());
}

/** ¿Contesta el servidor? `navigator.onLine` dice «sí» con el wifi sin salida. */
async function hayServidor(): Promise<boolean> {
  try {
    const r = await fetch("/version.json", { cache: "no-store" });
    return r.ok;
  } catch {
    return false;
  }
}

export async function traerLaNueva(
  recargar: () => void = () => window.location.reload(),
): Promise<"sin-internet" | void> {

  if (!("serviceWorker" in navigator)) {
    recargar();
    return;
  }

  try {
    const registro = await navigator.serviceWorker.getRegistration();

    if (registro) {
      // Por si el aviso vino de `version.json` y el service worker todavía no
      // se había enterado: se le pregunta ahora mismo.
      if (!registro.waiting) await registro.update().catch(() => undefined);

      /*
        ⚠️ Y SI SE ESTÁ DESCARGANDO, SE LA ESPERA.

        `update()` termina en cuanto el navegador ENCUENTRA la copia nueva, no
        cuando acaba de bajarla: con más de cien archivos, eso son varios
        segundos en los que la copia está «instalándose» y todavía no espera.
        Antes, en ese hueco se concluía que algo se había atascado y se iba al
        último recurso —borrarlo todo— con la copia nueva a medio instalar.

        De ahí salía la pantalla que se quedaba cargando para siempre (ver
        `public/sw-transicion.js`). Lo vio el dueño dos actualizaciones
        seguidas: «Actualizando…» y nada.
      */
      if (!registro.waiting && registro.installing) {
        await esperarQueTermine(registro.installing, ESPERA_DE_LA_DESCARGA);
      }

      if (registro.waiting) {
        /*
          El seguro de los dos segundos NO es adorno.

          Si el aviso de «ya tomé el mando» no llega —pasa en navegadores viejos
          y en algún WebView de Android— sin esto la pantalla se queda con el
          botón girando para siempre, que es peor que no haber avisado nada.
        */
        const seguro = window.setTimeout(recargar, 2000);
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          () => {
            window.clearTimeout(seguro);
            recargar();
          },
          { once: true },
        );
        registro.waiting.postMessage({ type: "SKIP_WAITING" });
        return;
      }

      /*
        ⚠️ OTRA PESTAÑA YA LA TRAJO.

        Con Softfi abierto en dos pestañas, la primera en actualizar activa la
        versión nueva para todas. En la segunda ya no queda ninguna esperando,
        y eso NO es que algo se atascó: basta con recargar. Antes se iba al
        último recurso y lo borraba todo sin necesidad.
      */
      if (yaCambioElMando) {
        recargar();
        return;
      }

      /*
        ⚠️ SIN INTERNET, NO SE BORRA NADA.

        El último recurso borra la copia guardada, y esa copia es lo que deja
        abrir Softfi sin señal. Borrarla sin poder bajar la nueva dejaba la
        caja sin arrancar justo cuando no hay internet — lo peor que le puede
        pasar a un punto de venta. Se avisa y se intenta cuando vuelva.
      */
      if (!(await hayServidor())) return "sin-internet";

      /*
        ⚠️ EL ÚLTIMO RECURSO.

        Si sabemos que hay una versión nueva —lo dijo `version.json`— y aun así
        no hay ninguna esperando, algo se atascó por dentro. Recargar volvería a
        servir la copia guardada, o sea nada.

        Así que se borra la copia: se quita el service worker y se vacían sus
        cachés. Al recargar, todo baja del servidor y el service worker se
        instala de nuevo solo. Se pierde el modo sin conexión durante esa
        recarga, y es un precio que vale la pena pagar cuando la alternativa es
        quedarse con una versión vieja para siempre.
      */
      await registro.unregister().catch(() => undefined);
    }

    if ("caches" in window) {
      const nombres = await caches.keys();
      // ⚠️ Menos la marca de «este aparato ya está al día». Borrarla hacía que
      // la siguiente copia creyera que venía a rescatar un aparato atascado
      // (ver `public/sw-transicion.js`), cuando no lo estaba.
      await Promise.all(
        nombres.filter((n) => !n.startsWith(MARCA_DE_AL_DIA)).map((n) => caches.delete(n)),
      );
    }
  } catch {
    // Si algo de esto falla, se recarga igual: mejor intentarlo que quedarse.
  }

  recargar();
}
